/**
 * SystemManagement Page
 * Refactored to use standardized PageLayout components
 */

import React, { useState, useEffect } from 'react';
import {
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Palette as PaletteIcon,
  Description as DescriptionIcon,
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../contexts/SnackbarContext';

import { useSystem } from '../hooks/api/config';
import { usePermissions } from '../hooks/permissions/usePermissions';
import { PageLayout } from '../components/ui/PageLayout';
import {
  AdminTabs,
  type AdminTab,
} from '../components/admin';
import type { SystemSettings, BrandingSettings as BrandingSettingsType } from '../hooks/api/config/useSystemOrganization';

// Import system settings components
import {
  SystemOverview,
  BrandingSettings,
  DocumentSettings,
  SecuritySettings,
  AuthProviderSettings,
  MailSettings,
} from '../components/system';

export default function SystemManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { system, isLoading, updateSystem, updateSettings, updateBranding } = useSystem();
  const { can, isLoading: permissionsLoading } = usePermissions();
  const [tabValue, setTabValue] = useState(0);

  // Check admin permission
  useEffect(() => {
    if (!permissionsLoading && !can.configureChatModels) {
      // Redirect if user doesn't have admin permissions
      navigate('/dashboard');
    }
  }, [can.configureChatModels, permissionsLoading, navigate]);

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <PageLayout
        title={t('system.management', 'System Management')}
        isLoading={false}
        usePaper={true}
      >
        <CircularProgress />
      </PageLayout>
    );
  }

  // Handler functions
  const handleSystemUpdate = async (updates: any) => {
    try {
      await updateSystem(updates);
      showSuccess(t('system.updateSuccess', 'System updated successfully'));
    } catch (error) {
      showError(t('system.updateError', 'Failed to update system'));
    }
  };

  const handleSettingsUpdate = async (settings: SystemSettings) => {
    try {
      await updateSettings(settings);
      showSuccess(t('system.settingsUpdateSuccess', 'Settings updated successfully'));
    } catch (error) {
      showError(t('system.settingsUpdateError', 'Failed to update settings'));
    }
  };

  const handleBrandingUpdate = async (branding: BrandingSettingsType) => {
    try {
      await updateBranding(branding);
      showSuccess(t('system.brandingUpdateSuccess', 'Branding updated successfully'));
    } catch (error) {
      showError(t('system.brandingUpdateError', 'Failed to update branding'));
    }
  };

  // Show error if system failed to load
  if (!isLoading && !system) {
    return (
      <PageLayout
        title={t('system.management', 'System Management')}
        isLoading={false}
        usePaper={true}
      >
        <Alert severity="error">
          {t('system.loadError', 'Failed to load system configuration')}
        </Alert>
      </PageLayout>
    );
  }

  // Define tabs with standardized structure
  const tabs: AdminTab[] = [
    {
      label: t('system.tabs.overview', 'Overview'),
      icon: <BusinessIcon />,
      content: (
          <SystemOverview
          system={system as NonNullable<typeof system>}
          onUpdate={handleSystemUpdate}
        />
      ),
    },
    {
      label: t('system.tabs.branding', 'Branding'),
      icon: <PaletteIcon />,
      content: (
        <BrandingSettings
          branding={system?.settings?.branding || {}}
          onUpdate={handleBrandingUpdate}
        />
      ),
    },
    {
      label: t('system.tabs.documents', 'Documents'),
      icon: <DescriptionIcon />,
      content: (
        <DocumentSettings
          settings={system?.settings?.limits || {}}
          onUpdate={(limits) => handleSettingsUpdate({ limits } as SystemSettings)}
        />
      ),
      disabled: true, // Keep disabled as per original
    },
    {
      label: t('system.tabs.security', 'Security'),
      icon: <SecurityIcon />,
      content: (
        <SecuritySettings
          security={system?.settings?.security || {}}
          onUpdate={(security) => handleSettingsUpdate({ security } as SystemSettings)}
        />
      ),
      disabled: true, // Keep disabled as per original
    },
    {
      label: t('system.tabs.authProviders', 'Auth Providers'),
      icon: <VpnKeyIcon />,
      content: (
        <AuthProviderSettings
          onUpdate={() => {
            showSuccess(t('auth.providers.saved', 'Auth providers updated successfully'));
          }}
        />
      ),
    },
    {
      label: t('system.tabs.mail', 'Mail'),
      icon: <EmailIcon />,
      content: (
        <MailSettings
          onUpdate={() => {
            showSuccess(t('system.mail.updateSuccess', 'Mail settings updated successfully'));
          }}
        />
      ),
    },
  ];

  return (
    <>
      <PageLayout
        title={t('system.management', 'System Management')}
        isLoading={isLoading}
        maxWidth="xl"
      >
        <AdminTabs
          tabs={tabs}
          value={tabValue}
          onChange={setTabValue}
          variant="scrollable"
        />
      </PageLayout>
    </>
  );
}
